import { getApiHosts, getDefaultHeaders, spaHeaderOptions, type Env } from '@fff-web/constants';
import { createRequest } from '@fff-web/request';
import type {
  BffClientInfo,
  GetCustomizeAccessParams,
  GetCustomizeAccessResponse,
  GetLiveSalesParams,
  GetLiveSalesResponse,
  GetNavbarProfileParams,
  GetNavbarProfileResponse,
  GetSaleProductsBody,
  GetSaleProductsParams,
  GetSaleProductsResponse,
} from './types';

type NavbarProfileContextHeaders = {
  [headerName: string]: string;
};

/**
 * Asado SDK.
 * @description
 * Asado SDK is a simple function that takes the environment as parameter (`env`) and
 * returns a set of functions to interact with the asado API. The url used
 * will depend on the environment passed when initializing the SDK.
 *
 * The functions from this SDK are meant to be used on any environment, so
 * the redirect and credentials properties are set to the default fetch values.
 * This is because API calls in Maui will always hit the NextJS API routes,
 * which will handle the redirection. This also means that credentials will
 * be sent only on same-origin requests.
 *
 * If you want to use this in the client, you can initialize
 * the SDK with the parameter `spa = true` to set the redirect policy to
 * manual and credentials to be always included. Please remember that this
 * is not recommended as it has potential security implications. In the future
 * this option will be removed.
 *
 * For more information about Asado APIs please check Swagger:
 * @see {link} https://asado.fff-dev.com/docs
 */
export function asadoSdk(env: Env, spa = false, host?: string, client?: BffClientInfo) {
  if (!env) {
    throw new Error('Environment is required.');
  }

  const options = spa ? spaHeaderOptions : {};
  if (!host) {
    const { ASADO_API_BASE_HOST } = getApiHosts(env, ['ASADO_API_BASE_HOST']);
    host = `${ASADO_API_BASE_HOST}`;
  }
  const defaultHeaders = {
    ...getDefaultHeaders(env, true),
    ...(client?.platform && { 'x-client-platform': client.platform }),
    ...(client?.version && { 'x-client-version': client.version }),
  };
  const baseSubDomain = 'bff/v1';

  return {
    getCustomizeAccess: (params: GetCustomizeAccessParams, jwtToken?: string) =>
      createRequest<GetCustomizeAccessParams, GetCustomizeAccessResponse>({
        url: `${host}/${baseSubDomain}/customize/access`,
        method: 'GET',
        fetchOptions: {
          headers: { ...defaultHeaders, ...(jwtToken && { Cookie: `Jwt=${jwtToken}` }) },
          ...options,
        },
      })(params),

    getNavbarProfile: (
      contextHeaders?: NavbarProfileContextHeaders,
      params?: GetNavbarProfileParams,
    ) =>
      createRequest<GetNavbarProfileParams | undefined, GetNavbarProfileResponse>({
        url: `${host}/${baseSubDomain}/navbar/profile`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(contextHeaders ?? {}),
          },
          ...options,
        },
      })(params),

    getSaleProducts: ({ saleUuid, forwardedHeaders, ...body }: GetSaleProductsParams) =>
      createRequest<GetSaleProductsBody, GetSaleProductsResponse>({
        url: `${host}/${baseSubDomain}/sales/${saleUuid}/products`,
        method: 'POST',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(body),

    getLiveSales: ({ preview, forwardedHeaders }: GetLiveSalesParams = {}) =>
      createRequest<{ preview?: boolean } | undefined, GetLiveSalesResponse>({
        url: `${host}/${baseSubDomain}/sales/live`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(preview !== undefined ? { preview } : undefined),
  };
}
